<template>
  <div class="">
    <!-- <CircularOverlay :overlay="!unreconciledPaymentsRecords" /> -->
    <v-container class="py-0">
      <transition name="fade" mode="out-in" appear>
        <PageTitle class="d-block d-sm-none">Client Accounts</PageTitle>
      </transition>

      <transition name="slide-left-fade" mode="out-in" appear>
        <v-tabs
          v-model="tab"
          class="mt-5 mt-sm-1"
          color="tabColor"
          slider-color="tabcolor"
          center-active
          background-color="tertiary"
          show-arrows
          height="55"
        >
          <v-tab ripple> Unreconciled </v-tab>
          <v-tab ripple> Reconciled </v-tab>
          <v-tab ripple> Payment Groups </v-tab>
          <v-tab ripple> Payment Requests </v-tab>
          <v-spacer></v-spacer>
          <span class="d-none d-sm-block">
            <PageTitle>Client accounts</PageTitle>
          </span>
        </v-tabs>
      </transition>

      <transition name="slide-fade" mode="out-in" appear>
        <div>
          <v-tabs-items class="pt-2 tertiary" v-model="tab">
            <v-tab-item>
              <Skeleton v-if="!unreconciledPayments || !unreconciledPayments.Data" />
              <Unreconciled 
                v-if="unreconciledPayments && unreconciledPayments.Data" 
                :isLoading="unreconciledLoading"
                :pagination="pagination.unreconciled"
                @paginate="handleUnreconciledPagination"
                @filter-change="handleUnreconciledFilterChange"
              />
            </v-tab-item>
            <v-tab-item>
              <Skeleton v-if="!reconciledStatements || !reconciledStatements.Data" />
              <ReconciledStatements 
                v-if="reconciledStatements && reconciledStatements.Data" 
                :isLoading="reconciledStatementsLoading"
                :pagination="pagination.reconciledStatements"
                @paginate="handleReconciledStatementsPagination"
                @filter-change="handleReconciledStatementsFilterChange"
              />
            </v-tab-item>
            <v-tab-item>
              <Skeleton v-if="!paymentGroups || !paymentGroups.Data" />
              <PaymentGroups 
                v-if="paymentGroups && paymentGroups.Data" 
                :isLoading="paymentGroupsLoading"
                :pagination="pagination.paymentGroups"
                @paginate="handlePaymentGroupsPagination"
                @filter-change="handlePaymentGroupsFilterChange"
              />
            </v-tab-item>
            <v-tab-item>
               <GenericRequestsTable />
            </v-tab-item>
          </v-tabs-items>
        </div>
      </transition>
    </v-container>
  </div>
</template>

<script>
import { mapState } from "vuex";
import Unreconciled from "../components/Unreconciled/UnreconciledPaymentRecords.vue";
import PaymentGroups from "../components/Unreconciled/PaymentGroupsRecords.vue";
import ReconciledStatements from "../components/Unreconciled/ReconciledStatementsRecords.vue";
import Skeleton from "../components/SkeletonLoaders/SkeletonLoaderTable.vue";
import PageTitle from "@/components/Slots/PageTitle.vue";
import GenericRequestsTable from "@/components/PaymentRequests/GenericRequestsTable.vue";

export default {
  components: {
    Unreconciled,
    PaymentGroups,
    ReconciledStatements,
    Skeleton,
    PageTitle,
    GenericRequestsTable,
  },
  data() {
    return {
      tab: null,
      pagination: {
        unreconciled: { page: 1, itemsPerPage: 10 },
        paymentGroups: { page: 1, itemsPerPage: 10 },
        reconciledStatements: { page: 1, itemsPerPage: 10 }
      },
      loadedTabs: {
        unreconciled: false,
        paymentGroups: false,
        reconciledStatements: false,
        paymentRequests: false
      },
      filters: {
        unreconciled: {
          fromDate: '',
          toDate: ''
        },
        reconciledStatements: {
          personId: '',
          fromDate: '',
          toDate: ''
        },
        paymentGroups: {
          state: '',
          fromDate: '',
          toDate: ''
        }
      }
    };
  },
  created() {
    // Only load the first tab on initial load
    this.loadUnreconciledPayments();
    this.loadedTabs.unreconciled = true;
  },
  watch: {
    tab(newTab) {
      // Load data for the selected tab if not already loaded
      const tabMap = {
        0: 'unreconciled',
        1: 'reconciledStatements',
        2: 'paymentGroups',
        3: 'paymentRequests'
      };
      
      const tabType = tabMap[newTab];
      if (tabType && !this.loadedTabs[tabType]) {
        this.loadTabData(tabType);
        this.loadedTabs[tabType] = true;
      }
    }
  },
  computed: {
    ...mapState({
      unreconciledPayments: (state) =>
        state.UnreconciledPayments.unreconciledPaymentsRecords,
      paymentGroups: (state) =>
        state.UnreconciledPayments.paymentGroupsRecords,
      reconciledStatements: (state) =>
        state.UnreconciledPayments.reconciledStatementsRecords,
      unreconciledLoading: (state) =>
        state.UnreconciledPayments.loading.unreconciled,
      paymentGroupsLoading: (state) =>
        state.UnreconciledPayments.loading.paymentGroups,
      reconciledStatementsLoading: (state) =>
        state.UnreconciledPayments.loading.reconciledStatements,
    }),
  },
  methods: {
    async loadTabData(tabType) {
      switch(tabType) {
        case 'unreconciled':
          await this.loadUnreconciledPayments();
          break;
        case 'paymentGroups':
          await this.loadPaymentGroups();
          break;
        case 'reconciledStatements':
          await this.loadReconciledStatements();
          break;
        // paymentRequests doesn't need loading as it's handled by the component itself
      }
    },
    async loadUnreconciledPayments(page = 1, itemsPerPage = null, fromDate = null, toDate = null) {
      if (itemsPerPage !== null) {
        this.pagination.unreconciled.itemsPerPage = itemsPerPage;
      }
      const offset = (page - 1) * this.pagination.unreconciled.itemsPerPage;
      
      // Use provided dates or fall back to stored filter values
      const finalFromDate = fromDate !== null ? fromDate : this.filters.unreconciled.fromDate;
      const finalToDate = toDate !== null ? toDate : this.filters.unreconciled.toDate;
      
      await this.$store.dispatch('UnreconciledPayments/getUnreconciledPaymentsRecords', {
        offset,
        limit: this.pagination.unreconciled.itemsPerPage,
        fromDate: finalFromDate,
        toDate: finalToDate,
        forceRefresh: itemsPerPage !== null || fromDate !== null || toDate !== null // Force refresh when filters change
      });
      this.pagination.unreconciled.page = page;
    },
    async loadPaymentGroups(page = 1, itemsPerPage = null, filters = null) {
      if (itemsPerPage !== null) {
        this.pagination.paymentGroups.itemsPerPage = itemsPerPage;
      }
      const offset = (page - 1) * this.pagination.paymentGroups.itemsPerPage;
      
      // Use provided filters or fall back to stored filter values
      const finalFilters = filters || this.filters.paymentGroups;
      
      await this.$store.dispatch('UnreconciledPayments/getPaymentGroupsRecords', {
        offset,
        limit: this.pagination.paymentGroups.itemsPerPage,
        ...finalFilters,
        forceRefresh: itemsPerPage !== null || filters !== null
      });
      this.pagination.paymentGroups.page = page;
    },
    async loadReconciledStatements(page = 1, itemsPerPage = null, filters = null) {
      if (itemsPerPage !== null) {
        this.pagination.reconciledStatements.itemsPerPage = itemsPerPage;
      }
      const offset = (page - 1) * this.pagination.reconciledStatements.itemsPerPage;
      
      // Use provided filters or fall back to stored filter values
      const finalFilters = filters || this.filters.reconciledStatements;
      
      await this.$store.dispatch('UnreconciledPayments/getReconciledStatementsRecords', {
        offset,
        limit: this.pagination.reconciledStatements.itemsPerPage,
        ...finalFilters,
        forceRefresh: itemsPerPage !== null || filters !== null
      });
      this.pagination.reconciledStatements.page = page;
    },
    handleUnreconciledPagination(page, itemsPerPage, fromDate, toDate) {
      // Mark tab as loaded when user interacts with pagination
      if (!this.loadedTabs.unreconciled) {
        this.loadedTabs.unreconciled = true;
      }
      
      // Update pagination state immediately for responsive UI
      this.pagination.unreconciled.page = page;
      if (itemsPerPage) {
        this.pagination.unreconciled.itemsPerPage = itemsPerPage;
      }
      
      // Pass dates if provided
      this.loadUnreconciledPayments(page, itemsPerPage, fromDate, toDate);
    },
    handleUnreconciledFilterChange(fromDate, toDate) {
      // Store the filter values
      this.filters.unreconciled.fromDate = fromDate;
      this.filters.unreconciled.toDate = toDate;
      
      // Reset to first page when filters change
      this.pagination.unreconciled.page = 1;
      
      // Load data with new filters
      this.loadUnreconciledPayments(1, null, fromDate, toDate);
    },
    handlePaymentGroupsPagination(page, itemsPerPage, filters) {
      // Mark tab as loaded when user interacts with pagination
      if (!this.loadedTabs.paymentGroups) {
        this.loadedTabs.paymentGroups = true;
      }
      
      // Update pagination state immediately for responsive UI
      this.pagination.paymentGroups.page = page;
      if (itemsPerPage) {
        this.pagination.paymentGroups.itemsPerPage = itemsPerPage;
      }
      
      // Pass filters if provided
      this.loadPaymentGroups(page, itemsPerPage, filters);
    },
    handlePaymentGroupsFilterChange(filters) {
      // Store the filter values
      this.filters.paymentGroups = { ...filters };
      
      // Reset to first page when filters change
      this.pagination.paymentGroups.page = 1;
      
      // Load data with new filters
      this.loadPaymentGroups(1, null, filters);
    },
    handleReconciledStatementsPagination(page, itemsPerPage, filters) {
      // Mark tab as loaded when user interacts with pagination
      if (!this.loadedTabs.reconciledStatements) {
        this.loadedTabs.reconciledStatements = true;
      }
      
      // Update pagination state immediately for responsive UI
      this.pagination.reconciledStatements.page = page;
      if (itemsPerPage) {
        this.pagination.reconciledStatements.itemsPerPage = itemsPerPage;
      }
      
      // Pass filters if provided
      this.loadReconciledStatements(page, itemsPerPage, filters);
    },
    handleReconciledStatementsFilterChange(filters) {
      // Store the filter values
      this.filters.reconciledStatements = { ...filters };
      
      // Reset to first page when filters change
      this.pagination.reconciledStatements.page = 1;
      
      // Load data with new filters
      this.loadReconciledStatements(1, null, filters);
    }
  }
};
</script>
